import React, { useEffect, useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  CircularProgress,
  Table,
  TablePagination,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ThemeProvider } from "@mui/material";
import theme from "../../theme";
import { GetOrderSummary, UpdateOrderStatus } from "../../api/order/OrderController";
import DataTableComponent from "../../components/Table/DataTableComponent";
import DetailModalComponent from "../../components/Modal/DetailModalComponent";
import tableHeaders from "../../data/tableHeader";
import DeleteDialogComponent from "../../components/Dialog/DeleteDialogComponent";

const steps = ["Order Created", "Viewing Order", "On Way", "Completed"];

const simulatedResponse = {
  status: "Completed",
};

const OrderStatus = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showIdColumns, setShowIdColumns] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("Ads");
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [page, setPage] = useState(0); // 0-based index
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [UserId, setUserId] = useState("");
  const [FilterPayload, setFilterPayload] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null); // New state for selected product
  const [showDetailModal, setShowDetailModal] = useState(false); // New state for modal visibility
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [cancelledOrderIds, setCancelledOrderIds] = useState([]);
  const [orderStatusFilter, setOrderStatusFilter] = useState("All");

  const getOrderTimestamp = (order) => {
    const rawValue =
      order?.orderDate ||
      order?.OrderDate ||
      order?.createdAt ||
      order?.CreatedAt ||
      order?.createdDate ||
      order?.CreatedDate ||
      order?.createDate ||
      order?.createDateTime ||
      order?.orderTime ||
      order?.createdOn;
    if (rawValue === undefined || rawValue === null) {
      return null;
    }
    const timestamp = new Date(rawValue).getTime();
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }
    if (typeof rawValue === "number") {
      return rawValue;
    }
    return null;
  };

  const handleMoreClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewDetails = (product, category) => {
    console.log("Selected Product to view detail", product);
    console.log("Selected category to view details", category);
    setSelectedProduct(product);
    setSelectedCategory(category);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedProduct(null);
  };

  const handleOpenDeleteDialog = (row) => {
    setSelectedRow(row);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const orderId =
      selectedRow.order_Id || selectedRow.orderId || selectedRow.OrderId;
    if (!orderId) return;
    const response = await UpdateOrderStatus(orderId, "Cancelled", setLoading);
    if (response?.baseResponseModel?.respCode === 200) {
      setOpenDeleteDialog(false);
      setCancelledOrderIds((prev) => [...prev, orderId]);
      setData((prev) =>
        prev.filter(
          (order) => (order.order_Id || order.orderId || order.OrderId) !== orderId
        )
      );
    }
  };
  useEffect(() => {
    getOrderStatus();
  }, []);

  const getOrderStatus = async () => {
    try {
      const response = await GetOrderSummary(setLoading);
      console.log("Order Status:", response);
      const orderList = response.orderListSummary || [];
      const cancelledSet = new Set(cancelledOrderIds);
      const filteredOrders = orderList.filter((order) => {
        const status = String(order.orderStatus || order.OrderStatus || "").toLowerCase();
        const orderId = order.order_Id || order.orderId || order.OrderId;
        return status !== "cancelled" && !cancelledSet.has(orderId);
      });
      const sortedOrders = filteredOrders.slice().sort((a, b) => {
        const aTimestamp = getOrderTimestamp(a);
        const bTimestamp = getOrderTimestamp(b);
        if (aTimestamp === null && bTimestamp === null) {
          return 0;
        }
        if (aTimestamp === null) {
          return 1;
        }
        if (bTimestamp === null) {
          return -1;
        }
        return bTimestamp - aTimestamp;
      });
      setData(sortedOrders);
    } catch (err) {
      console.error("Error fetching order status:", err);
    } finally {
      setLoading(false);
    }
  };

  const matchesStatusFilter = (order) => {
    if (orderStatusFilter === "All") {
      return true;
    }
    const rawStatus = String(
      order.orderStatus || order.OrderStatus || ""
    ).trim();
    const normalizedStatus = rawStatus.toLowerCase();
    const filterValue = orderStatusFilter.toLowerCase();
    if (filterValue === "view") {
      return normalizedStatus === "view" || normalizedStatus.startsWith("view");
    }
    return normalizedStatus === filterValue;
  };

  const filteredData = data.filter(matchesStatusFilter);
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            width: "100%",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} color="primary" />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="App">
        <div style={{ marginBottom: "20px" }}>
          {/* Other category buttons can be added here if needed */}
        </div>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 1.5,
            px: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: theme.palette.text.main }}
          >
            Filter by Order Status
          </Typography>
          <ToggleButtonGroup
            value={orderStatusFilter}
            exclusive
            onChange={(_, value) => value && setOrderStatusFilter(value)}
            size="small"
            sx={{
              backgroundColor: theme.palette.secondary.main,
              borderRadius: "999px",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 0.5,
                border: 0,
                borderRadius: "999px !important",
                color: theme.palette.text.main,
              },
              "& .Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
            }}
          >
            <ToggleButton value="All">All</ToggleButton>
            <ToggleButton value="New">New</ToggleButton>
            <ToggleButton value="View">View</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <DataTableComponent
          data={filteredData}
          showIdColumns={showIdColumns}
          handleViewDetails={handleViewDetails}
          tableHeaders={tableHeaders["OrderSummary"]}
          isLoading={loading}
          setIsFilter={setIsFilterDrawerOpen}
          handleOpenDeleteDialog={handleOpenDeleteDialog}
          showNewAddButton={false}
          requested={"Order"}
        />
        <Button
          onClick={() => setShowIdColumns(!showIdColumns)}
          style={{ marginTop: "20px" }}
        >
          {showIdColumns ? "Hide ID Columns" : "Show ID Columns"}
        </Button>
        <DetailModalComponent
          open={showDetailModal}
          onClose={handleCloseDetailModal}
          data={selectedProduct}
          category="product"
        />
        <DeleteDialogComponent
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onDelete={handleDelete}
        />
      </div>
    </ThemeProvider>
  );
};

export default OrderStatus;
